// src/components/charts/PerformanceAnalysis.jsx
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Users, 
  Award, 
  Radar,
  AlertCircle
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { StatisticsCalculator } from '../../utils/statistics';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { processMarkAnalytics } from '../../utils/processMarksAnalytic';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PerformanceAnalysis = ({ taskId }) => {
  const [marks, setMarks] = useState([]);
  const [performanceLevels, setPerformanceLevels] = useState([]);
  const [fiveNumberSummary, setFiveNumberSummary] = useState(null);
  const [questionPerformance, setQuestionPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (taskId) {
      loadAnalysisData();
    }
  }, [taskId]);

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      
      // Load task details
      const taskData = await AssessmentService.getAssessmentTask(taskId);
      setTask(taskData);
      
      // Load learner marks
      const learnerMarks = await AssessmentService.getLearnerMarks(taskId);
      
      if (learnerMarks.length > 0) {
        setMarks(learnerMarks);
        
        // Calculate performance levels
        const levels = StatisticsCalculator.calculatePerformanceLevels(learnerMarks);
        setPerformanceLevels(levels);
        
        // Calculate five number summary
        const percentages = learnerMarks.map(m => m.percentageMark);
        const summary = StatisticsCalculator.calculateFiveNumberSummary(percentages);
        setFiveNumberSummary(summary);
        
        // Calculate question performance
        const qPerformance = StatisticsCalculator.calculateQuestionPerformance(learnerMarks);
        setQuestionPerformance(qPerformance);
      }

    } catch (error) {
      console.error('Error loading analysis data:', error);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
        if (task && marks.length > 0) {
          try {
            const analyticsData = processMarkAnalytics(marks, task);
            setAnalytics(analyticsData);
          } catch (error) {
            console.error('Error processing analytics:', error);
          }
        }
      }, [task, marks]);

  // Add rendering functions for each dimension
  const renderTaxonomyAnalysis = () => {
    if (!analytics || Object.values(analytics.taxonomy).every(level => level.count === 0)) {
      return null;
    }
    
    // Convert taxonomy data to array for sorting
    const taxonomyData = Object.entries(analytics.taxonomy)
      .map(([key, data]) => ({
        key,
        name: data.name,
        average: data.average,
        count: data.count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => a.key.localeCompare(b.key)); // Sort by level
    
    // Chart data
    const chartData = {
      labels: taxonomyData.map(item => item.name),
      datasets: [{
        label: 'Average Performance',
        data: taxonomyData.map(item => item.average),
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)', // L1 - Remember
          'rgba(16, 185, 129, 0.7)', // L2 - Understand
          'rgba(245, 158, 11, 0.7)', // L3 - Apply
          'rgba(239, 68, 68, 0.7)'   // L4 - Analyze
        ],
        borderWidth: 1
      }]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Performance by Cognitive Level',
          font: {
            size: 14
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Average: ${context.raw.toFixed(1)}% (${taxonomyData[context.dataIndex].count} questions)`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Performance (%)'
          },
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Cognitive Level Analysis</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-72">
              <Bar data={chartData} options={chartOptions} />
            </div>
            
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cognitive Level</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Visual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {taxonomyData.map(item => (
                    <tr key={item.key}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium">{item.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.average.toFixed(1)}%</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.count}</td>
                      <td className="px-3 py-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              item.average >= 70 ? 'bg-green-500' :
                              item.average >= 50 ? 'bg-blue-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${item.average}%` }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Cognitive Levels Legend</h4>
                <ul className="space-y-1 text-xs text-gray-700">
                  <li><strong>L1 (Remember):</strong> Recall facts and basic concepts</li>
                  <li><strong>L2 (Understand):</strong> Explain ideas or concepts</li>
                  <li><strong>L3 (Apply):</strong> Use information in new situations</li>
                  <li><strong>L4 (Analyze):</strong> Draw connections among ideas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTopicAnalysis = () => {
    if (!analytics || Object.keys(analytics.topics).length === 0) {
      return null;
    }
    
    // Convert topic data to array for sorting
    const topicData = Object.entries(analytics.topics)
      .map(([topic, data]) => ({
        topic,
        average: data.average,
        count: data.count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.average - a.average); // Sort by performance
    
    // Chart data
    const chartData = {
      labels: topicData.map(item => item.topic),
      datasets: [{
        label: 'Average Performance',
        data: topicData.map(item => item.average),
        backgroundColor: topicData.map(item => {
          if (item.average >= 80) return 'rgba(34, 197, 94, 0.7)'; // green
          if (item.average >= 70) return 'rgba(59, 130, 246, 0.7)'; // blue
          if (item.average >= 60) return 'rgba(234, 179, 8, 0.7)';  // yellow
          if (item.average >= 50) return 'rgba(249, 115, 22, 0.7)'; // orange
          return 'rgba(239, 68, 68, 0.7)'; // red
        }),
        borderWidth: 1
      }]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y', // Horizontal bar chart
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Performance by Topic',
          font: {
            size: 14
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Average: ${context.raw.toFixed(1)}% (${topicData[context.dataIndex].count} questions)`;
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Performance (%)'
          },
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        },
        y: {
          title: {
            display: true,
            text: 'Topic'
          }
        }
      }
    };
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Topic Performance Analysis</h3>
        </div>
        <div className="p-6">
          <div className="h-96">
            <Bar data={chartData} options={chartOptions} />
          </div>
          
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-green-600" />
                  Strongest Topics
                </h4>
                <div className="space-y-4">
                  {topicData.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.topic}</div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                          <div 
                            className="bg-green-600 h-2.5 rounded-full" 
                            style={{ width: `${item.average}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ml-4 text-sm font-medium text-green-700">
                        {item.average.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  Areas for Improvement
                </h4>
                <div className="space-y-4">
                  {topicData.slice(-3).reverse().map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.topic}</div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                          <div 
                            className="bg-red-600 h-2.5 rounded-full" 
                            style={{ width: `${item.average}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ml-4 text-sm font-medium text-red-700">
                        {item.average.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSubtopicAnalysis = () => {
    if (!analytics || Object.keys(analytics.subtopics).length === 0) {
      return null;
    }
    
    // Convert subtopic data to array for sorting
    const subtopicData = Object.entries(analytics.subtopics)
      .map(([key, data]) => ({
        key,
        topic: data.topic,
        subtopic: data.subtopic,
        average: data.average,
        count: data.count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => a.topic.localeCompare(b.topic) || a.subtopic.localeCompare(b.subtopic));
    
    // Group by topic
    const groupedSubtopics = {};
    subtopicData.forEach(item => {
      if (!groupedSubtopics[item.topic]) {
        groupedSubtopics[item.topic] = [];
      }
      groupedSubtopics[item.topic].push(item);
    });
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Subtopic Analysis</h3>
        </div>
        <div className="p-6">
          <div className="space-y-8">
            {Object.entries(groupedSubtopics).map(([topic, subtopics]) => (
              <div key={topic}>
                <h4 className="font-medium text-gray-900 mb-3">{topic}</h4>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subtopic</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Average</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {subtopics.map(item => (
                      <tr key={item.key}>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">{item.subtopic}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.average >= 70 ? 'bg-green-100 text-green-800' :
                            item.average >= 50 ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.average.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{item.count}</td>
                        <td className="px-3 py-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                item.average >= 70 ? 'bg-green-500' :
                                item.average >= 50 ? 'bg-blue-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${item.average}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderConceptAnalysis = () => {
    if (!analytics || Object.keys(analytics.concepts).length === 0) {
      return null;
    }
    
    // Convert concepts data to array for sorting
    const conceptData = Object.entries(analytics.concepts)
      .map(([concept, data]) => ({
        concept,
        average: data.average,
        count: data.count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.average - a.average); // Sort by performance
    
    // Only show top 10 concepts to keep display manageable
    const topConcepts = conceptData.slice(0, 10);
    
    // Chart data
    const chartData = {
      labels: topConcepts.map(item => item.concept),
      datasets: [{
        label: 'Average Performance',
        data: topConcepts.map(item => item.average),
        backgroundColor: topConcepts.map(item => {
          if (item.average >= 80) return 'rgba(34, 197, 94, 0.7)'; // green
          if (item.average >= 70) return 'rgba(59, 130, 246, 0.7)'; // blue
          if (item.average >= 60) return 'rgba(234, 179, 8, 0.7)';  // yellow
          if (item.average >= 50) return 'rgba(249, 115, 22, 0.7)'; // orange
          return 'rgba(239, 68, 68, 0.7)'; // red
        }),
        borderWidth: 1
      }]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y', // Horizontal bar chart
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Performance by Key Concept',
          font: {
            size: 14
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Average: ${context.raw.toFixed(1)}% (${topConcepts[context.dataIndex].count} questions)`;
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Performance (%)'
          },
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Key Concepts Analysis</h3>
        </div>
        <div className="p-6">
          <div className="h-96">
            <Bar data={chartData} options={chartOptions} />
          </div>
          
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-green-600" />
                  Strong Concepts
                </h4>
                <div className="space-y-3">
                  {conceptData.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.concept}</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${item.average}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ml-4 text-sm font-medium text-green-700">
                        {item.average.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  Concepts Needing Reinforcement
                </h4>
                <div className="space-y-3">
                  {conceptData.slice(-5).reverse().map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.concept}</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-red-600 h-2 rounded-full" 
                            style={{ width: `${item.average}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ml-4 text-sm font-medium text-red-700">
                        {item.average.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSkillAnalysis = () => {
    if (!analytics || Object.keys(analytics.skills).length === 0) {
      return null;
    }
    
    // Convert skills data to array for sorting
    const skillData = Object.entries(analytics.skills)
      .map(([skill, data]) => ({
        skill,
        average: data.average,
        count: data.count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.average - a.average); // Sort by performance
    
    // Only show top 10 skills to keep display manageable
    const topSkills = skillData.slice(0, 10);
    
    // Chart data - use radar chart for skills
    const radarData = {
      labels: topSkills.map(item => item.skill),
      datasets: [{
        label: 'Performance',
        data: topSkills.map(item => item.average),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(59, 130, 246)',
        borderWidth: 2
      }]
    };
    
    const radarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          angleLines: {
            display: true
          },
          suggestedMin: 0,
          suggestedMax: 100,
          ticks: {
            stepSize: 20,
            callback: function(value) {
              return value + '%';
            }
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Skill Performance Profile',
          font: {
            size: 14
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.label}: ${context.raw.toFixed(1)}% (${topSkills[context.dataIndex].count} questions)`;
            }
          }
        }
      }
    };
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Skills Analysis</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-96">
              <Radar data={radarData} options={radarOptions} />
            </div>
            
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Skill</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Occurrence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {skillData.map((item, index) => (
                    <tr key={index} className={index < 3 ? 'bg-green-50' : (index >= skillData.length - 3 ? 'bg-red-50' : '')}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium">{item.skill}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.average >= 70 ? 'bg-green-100 text-green-800' :
                          item.average >= 50 ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.average.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.count} questions</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Skill Analysis Insights</h4>
                <p className="text-xs text-gray-700">
                  Skills appearing in multiple questions provide a more reliable measure of learner capability. 
                  Consider targeting instruction toward the lowest-performing skills to improve overall outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={loadAnalysisData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No marks data available for analysis.</p>
      </div>
    );
  }

  // Chart.js Options and Data
  
  // 1. Performance Levels Doughnut Chart
  const performanceDistributionData = {
    labels: performanceLevels.map(level => `Level ${level.level} (${level.range})`),
    datasets: [
      {
        data: performanceLevels.map(level => level.count),
        backgroundColor: [
          'rgba(239, 68, 68, 0.7)',  // red for level 1-2
          'rgba(249, 115, 22, 0.7)', // orange for level 3
          'rgba(234, 179, 8, 0.7)',  // yellow for level 4
          'rgba(59, 130, 246, 0.7)', // blue for level 5
          'rgba(16, 185, 129, 0.7)', // green for level 6
          'rgba(5, 150, 105, 0.7)',  // emerald for level 7
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(234, 179, 8, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(5, 150, 105, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };
  
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 15,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: true,
        text: 'Performance Level Distribution',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.formattedValue;
            const total = marks.length;
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${value} learners (${percentage}%)`;
          }
        }
      }
    },
    cutout: '60%'
  };

  // 2. Box Plot (using Line Chart for simplicity)
  const boxPlotData = {
    labels: [''],
    datasets: [
      {
        label: 'Min to Max Range',
        data: [fiveNumberSummary.minimum, fiveNumberSummary.maximum],
        borderColor: 'rgba(156, 163, 175, 0.5)',
        borderWidth: 2,
        pointRadius: 0,
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        fill: true
      },
      {
        label: 'Q1 to Q3 Range',
        data: [fiveNumberSummary.q1, fiveNumberSummary.q3],
        borderColor: 'rgba(59, 130, 246, 0.8)',
        borderWidth: 2,
        pointRadius: 0,
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        fill: true
      },
      {
        label: 'Median',
        data: [fiveNumberSummary.median],
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        pointRadius: 6,
        pointStyle: 'rectRot',
        pointBackgroundColor: 'rgba(59, 130, 246, 1)'
      },
      {
        label: 'Mean',
        data: [fiveNumberSummary.mean],
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2,
        pointRadius: 6,
        pointStyle: 'circle',
        pointBackgroundColor: 'rgba(239, 68, 68, 1)'
      }
    ]
  };

  const boxPlotOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Performance (%)'
        }
      },
      x: {
        display: false
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: 'Performance Distribution',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const datasetLabel = context.dataset.label;
            const value = context.formattedValue;
            
            if (datasetLabel === 'Median') {
              return `Median: ${value}%`;
            } else if (datasetLabel === 'Mean') {
              return `Mean: ${value}%`;
            } else if (datasetLabel === 'Q1 to Q3 Range') {
              if (context.dataIndex === 0) {
                return `Q1 (25%): ${value}%`;
              } else {
                return `Q3 (75%): ${value}%`;
              }
            } else if (datasetLabel === 'Min to Max Range') {
              if (context.dataIndex === 0) {
                return `Minimum: ${value}%`;
              } else {
                return `Maximum: ${value}%`;
              }
            }
            return `${datasetLabel}: ${value}%`;
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.4
      }
    }
  };

  // 3. Question Performance Bar Chart
  const questionPerformanceData = {
    labels: questionPerformance.map(q => `Q${q.questionNumber}`),
    datasets: [
      {
        label: 'Average Performance',
        data: questionPerformance.map(q => q.averagePercentage),
        backgroundColor: questionPerformance.map(q => {
          if (q.averagePercentage >= 80) return 'rgba(34, 197, 94, 0.7)'; // green
          if (q.averagePercentage >= 70) return 'rgba(59, 130, 246, 0.7)'; // blue
          if (q.averagePercentage >= 60) return 'rgba(234, 179, 8, 0.7)';  // yellow
          if (q.averagePercentage >= 50) return 'rgba(249, 115, 22, 0.7)'; // orange
          return 'rgba(239, 68, 68, 0.7)'; // red
        }),
        borderWidth: 1,
      }
    ]
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Average Performance by Question',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Average: ${context.raw.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Average Percentage'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Question'
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Task Information */}
      {task && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-2">{task.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-600">Type:</span>
              <span className="ml-2 text-sm font-medium">{task.type}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Grade:</span>
              <span className="ml-2 text-sm font-medium">{task.grade}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Subject:</span>
              <span className="ml-2 text-sm font-medium">{task.subject}</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-blue-800">Total Learners</h3>
          </div>
          <p className="text-2xl font-bold text-blue-900">{marks.length}</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h3 className="font-medium text-green-800">Class Average</h3>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {fiveNumberSummary ? fiveNumberSummary.mean.toFixed(1) : 0}%
          </p>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-yellow-600" />
            <h3 className="font-medium text-yellow-800">Median</h3>
          </div>
          <p className="text-2xl font-bold text-yellow-900">
            {fiveNumberSummary ? fiveNumberSummary.median.toFixed(1) : 0}%
          </p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <PieChart className="w-5 h-5 text-purple-600" />
            <h3 className="font-medium text-purple-800">Std Deviation</h3>
          </div>
          <p className="text-2xl font-bold text-purple-900">
            {fiveNumberSummary ? fiveNumberSummary.standardDeviation.toFixed(1) : 0}
          </p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Distribution Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Distribution</h3>
          <div className="h-80">
            <Doughnut data={performanceDistributionData} options={doughnutOptions} />
          </div>
        </div>

        {/* Five Number Summary and Box Plot */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Range Analysis</h3>
          <div className="h-80">
            {fiveNumberSummary && (
              <Line data={boxPlotData} options={boxPlotOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Performance Levels Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Performance Level Analysis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Range</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visual</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceLevels.map(level => (
                <tr key={level.level}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Level {level.level}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {level.range}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                   {level.description}
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                   {level.count}
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                   {level.percentage.toFixed(1)}%
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap">
                   <div className="w-full bg-gray-200 rounded-full h-2">
                     <div 
                       className={`h-2 rounded-full ${
                         level.level >= 6 ? 'bg-green-500' :
                         level.level >= 4 ? 'bg-blue-500' :
                         level.level >= 3 ? 'bg-yellow-500' : 'bg-red-500'
                       }`}
                       style={{ width: `${level.percentage}%` }}
                     ></div>
                   </div>
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
     </div>

     {/* Add new analysis sections */}
      {analytics && (
        <>
          {renderTaxonomyAnalysis()}
          {renderTopicAnalysis()}
          {renderSubtopicAnalysis()}
          {renderConceptAnalysis()}
          {renderSkillAnalysis()}
        </>
      )}

     {/* Five Number Summary Data */}
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
       <div className="bg-white rounded-lg shadow p-6">
         <h3 className="text-lg font-medium text-gray-900 mb-4">Five Number Summary</h3>
         {fiveNumberSummary && (
           <div className="space-y-3">
             <div className="flex justify-between">
               <span className="text-gray-600">Minimum:</span>
               <span className="font-medium">{fiveNumberSummary.minimum.toFixed(1)}%</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-600">Q1 (25th percentile):</span>
               <span className="font-medium">{fiveNumberSummary.q1.toFixed(1)}%</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-600">Median (Q2):</span>
               <span className="font-medium">{fiveNumberSummary.median.toFixed(1)}%</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-600">Q3 (75th percentile):</span>
               <span className="font-medium">{fiveNumberSummary.q3.toFixed(1)}%</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-600">Maximum:</span>
               <span className="font-medium">{fiveNumberSummary.maximum.toFixed(1)}%</span>
             </div>
             <hr className="my-3" />
             <div className="flex justify-between">
               <span className="text-gray-600">Mean:</span>
               <span className="font-medium">{fiveNumberSummary.mean.toFixed(1)}%</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-600">Standard Deviation:</span>
               <span className="font-medium">{fiveNumberSummary.standardDeviation.toFixed(1)}</span>
             </div>
           </div>
         )}
       </div>

       {/* Question Performance Analysis */}
       <div className="bg-white rounded-lg shadow p-6">
         <h3 className="text-lg font-medium text-gray-900 mb-4">Question Performance Analysis</h3>
         <div className="h-80">
           <Bar data={questionPerformanceData} options={barChartOptions} />
         </div>
       </div>
     </div>

     {/* Question Performance Detail Table */}
     <div className="bg-white rounded-lg shadow">
       <div className="px-6 py-4 border-b border-gray-200">
         <h3 className="text-lg font-medium text-gray-900">Question Statistics</h3>
       </div>
       <div className="p-6">
         <div className="overflow-x-auto">
           <table className="w-full text-sm">
             <thead className="bg-gray-50">
               <tr>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Q#</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Min</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Q1</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Med</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Q3</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Max</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg</th>
                 <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">StdDev</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-gray-200">
               {questionPerformance.map(q => (
                 <tr key={q.questionNumber}>
                   <td className="px-3 py-2 font-medium">Q{q.questionNumber}</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.minimum.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.q1.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.median.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.q3.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.maximum.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.mean.toFixed(0)}%</td>
                   <td className="px-3 py-2">{q.fiveNumberSummary.standardDeviation.toFixed(1)}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
       </div>
     </div>
   </div>
 );
};

export default PerformanceAnalysis;